'use client';

import { FormEvent, useRef, useState } from 'react';
import { Typography } from '@/components/atoms/Typography/Typography';
import { LoadingSpinner } from '@/components/atoms/AnimatedIcons/LoadingSpinner/LoadingSpinner';
import {
  InputField,
  InputFieldRef,
} from '@/components/atoms/InputField/InputField';
import { Button } from '@/components/atoms/Button/Button';
import { Image } from '@/components/atoms/Image/Image';
import { toggleModal } from '@/utils/generic';
import { events } from '@/utils/events';
import { usePageStore } from '@/store/usePageStore';
import { fetchUser } from '@/directus/services/common/auth/auth.fetch';
import * as Styled from './LogInForm.styled';
import { useTranslations } from 'next-intl';

export const LogInOut = () => {
  // const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const stateSelector = usePageStore;
  const isUserLogged = stateSelector((s) => s.isUserLogged);
  const setIsUserLogged = stateSelector((s) => s.setIsUserLogged);
  const emailRef = useRef<InputFieldRef>(null);
  const passwordRef = useRef<InputFieldRef>(null);
  const [isErrored, setIsErrored] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const t = useTranslations('login');

  const handleEmailChange = () => {
    setIsErrored(false);
  };

  const handlePasswordChange = () => {
    setIsErrored(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isUserLogged) {
      const email = emailRef.current?.getInputValue() || '';
      const password = passwordRef.current?.getInputValue() || '';

      if (email && password) {
        setIsLoading(true);

        try {
          const authResponse = await fetchUser({
            email: email,
            password: password,
          });

          if (authResponse && authResponse.success === true) {
            setIsUserLogged(true);
            toggleModal(events.toggleLoginModal);
          }
        } catch {
          emailRef.current?.updateInputValue(email);

          setIsErrored(true);
          if (emailRef.current) {
            formRef.current?.reset();
            emailRef.current.updateInputValue(email);
          }
        }

        setIsLoading(false);
      }
    }
  };

  const LogInForm = () => {
    return (
      <>
        <Typography text={t('title')} size="xl" />
        <Styled.Form onSubmit={handleSubmit} ref={formRef}>
          <InputField
            type={'email'}
            placeholder={'email'}
            callbackOnChange={handleEmailChange}
            stylesApplied="loginInput"
            animatePlaceHolder={true}
            name="email"
            ref={emailRef}
          />
          <InputField
            type={'password'}
            placeholder={'password'}
            callbackOnChange={handlePasswordChange}
            stylesApplied="loginInput"
            animatePlaceHolder={true}
            name="password"
            ref={passwordRef}
          />
          <Styled.FormButton type="submit">
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Typography
                as="span"
                text={t('button')}
                color="white"
                size="lg"
              />
            )}
          </Styled.FormButton>

          {isErrored && (
            <Typography
              text={t('errorMessage')}
              size="lg"
              className="text-error"
            />
          )}
        </Styled.Form>
      </>
    );
  };

  const LogOutConfirmation = () => {
    return (
      <Styled.LogOutConfirmation>
        <Typography text={t('logOutQuestion')} size="lg" />
        <Styled.FormButton onClick={() => toggleModal(events.toggleLoginModal)}>
          <Typography
            as="span"
            text={t('buttonStay')}
            color="primary"
            size="lg"
          />
        </Styled.FormButton>
        <Styled.FormButton
          onClick={(e) => {
            e.preventDefault();
            setIsUserLogged(false);
            toggleModal(events.toggleLoginModal);
            document.cookie = 'bg_user=; Max-Age=0; path=/;';
          }}
        >
          <Typography as="span" text={'Yes'} color="white" size="lg" />
        </Styled.FormButton>
      </Styled.LogOutConfirmation>
    );
  };

  return (
    <Styled.FormContainer>
      <Button
        className={'close-btn'}
        scaleOnHover={true}
        callbackOnClick={() => toggleModal(events.toggleLoginModal)}
      >
        <Image
          src="/img/icons/cross.svg"
          alt="cross"
          height={27}
          width={27}
          fill={false}
        />
      </Button>
      {isUserLogged ? <LogOutConfirmation /> : <LogInForm />}
    </Styled.FormContainer>
  );
};
